import Axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { API_URL } from 'src/config';
import { type Token, getAccessToken, type TokenResponse } from 'src/features/auth';
import { LOCALE } from 'src/providers/types';
import { storage } from 'src/utils/storage';

const axios = Axios.create({ baseURL: API_URL, paramsSerializer: { indexes: null } });

axios.interceptors.request.use((config) => {
    const { access: token } = storage.getToken();
    const language = storage.getLanguage() || LOCALE.BG;

    if (config.headers) {
        if (token) config.headers.authorization = `Bearer ${token}`;
        config.headers.Accept = 'application/json';
        config.headers['Accept-Language'] = language;
    }
    return config;
});

type ExtendedAxiosRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

axios.interceptors.response.use(
    (response: AxiosResponse) => {
        return response.data;
    },
    async (error: AxiosError) => {
        const originalRequest: ExtendedAxiosRequestConfig = error.config as ExtendedAxiosRequestConfig;
        // Handles the case where the access token has expired and refreshes the token
        // Attempts the request again with the new access token
        if (error.response?.status === 401 && !originalRequest?._retry) {
            originalRequest._retry = true;
            const { access, refresh } = storage.getToken();
            if (access == null || refresh == null) {
                // redirect('/login');
                return Promise.reject(error);
            }

            const { exp: refreshTokenExpiry } = jwtDecode<Token>(refresh);
            const { exp: accessTokenExpiry } = jwtDecode<Token>(access);
            const currentSeconds = Math.floor(new Date().getTime() / 1000);

            if (refreshTokenExpiry < currentSeconds) {
                // Clear the token from the storage as it can't be refreshed
                storage.clearToken();
                // redirect('/login');
                return Promise.reject(error);
            } else if (accessTokenExpiry < currentSeconds) {
                const tokenResponse = await getAccessToken({ refresh });
                if ('code' in tokenResponse && tokenResponse.code === 'token_not_valid') {
                    storage.clearToken();
                } else {
                    const { access } = tokenResponse as TokenResponse;
                    storage.setToken({ access });
                }
            }
            return axios(originalRequest);
        }
        return Promise.reject(error);
    },
);

export default axios;
